import React from 'react';
import classes from './WeatherChip.module.css';

const ChevronIcon = ({ expanded }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none"
    style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }}
  >
    <path d="M6 9L12 15L18 9" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Derives the same emoji/temperature the full WeatherCard shows, from the
// shared useWeatherData() result — kept here (not in the hook) since it's
// purely about how the collapsed chip presents that data, not about fetching
// it. Distinguishes "still resolving, might have something" from "nothing
// will ever show" so the chip can render a skeleton for the former instead
// of nothing — most fetches resolve from useWeatherData's own cache almost
// instantly, but the very first fetch of the day still takes a real
// network round-trip, and popping the whole chip into existence once that
// lands reads as a layout jump rather than a load.
export const getWeatherChipInfo = (weatherData) => {
  const {
    showOverride, showLive, showClimate,
    forecastDays, forecastLoading, forecastFailed,
    climateData, climateLoading, climateFailed,
    lat, lng, startdate,
  } = weatherData;

  if (showOverride) {
    return { state: 'ready', emoji: '⛅', temp: null, isLive: false };
  }

  if (showLive) {
    if (forecastFailed) return { state: 'hidden' };
    if (forecastLoading || !forecastDays) return { state: 'loading' };
    if (forecastDays.length === 0) return { state: 'hidden' };
    return { state: 'ready', emoji: forecastDays[0].emoji, temp: forecastDays[0].temp, isLive: true };
  }

  // Climate average — same early-outs WeatherCard itself uses for this branch.
  if (!lat || !lng || !startdate) return { state: 'hidden' };
  if (climateFailed) return { state: 'hidden' };
  if (climateLoading || !climateData) return { state: 'loading' };
  return { state: 'ready', emoji: '☀️', temp: climateData.avgHigh, isLive: false };
};

const WeatherChip = ({ weatherData, expanded, onToggle }) => {
  const info = getWeatherChipInfo(weatherData);

  if (info.state === 'hidden') return null;

  if (info.state === 'loading') {
    return <span className={classes.chipSkeleton} aria-hidden="true" />;
  }

  return (
    <button className={classes.chip} onClick={onToggle} aria-expanded={expanded}>
      {info.isLive && <span className={classes.liveDot} aria-hidden="true" />}
      <span className={classes.emoji}>{info.emoji}</span>
      {info.temp !== null && <span className={classes.temp}>{info.temp}°</span>}
      <ChevronIcon expanded={expanded} />
    </button>
  );
};

export default WeatherChip;
